import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Collapse,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Grid,
  Card,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add,
  ArrowBack,
  Delete,
  Edit,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Store,
} from '@mui/icons-material';

const NAVY = '#0D1B35';
const CYAN = '#06B6D4';
import apiClient from '../lib/axios';

enum IntegrationType {
  CENTRAL = 'CENTRAL',
  DISTRIBUTED = 'DISTRIBUTED',
}

interface PharmacyChain {
  id: string;
  name: string;
  integrationType: IntegrationType;
  baseURL: string | null;
  apiKey: string | null;
  logoUrl: string | null;
  phoneNumber: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  branchCount?: number;
}

interface PharmacyBranch {
  id: string;
  chainId: string;
  name: string;
  remoteBranchCode: string | null;
  directBaseURL: string | null;
  address: string;
  location: { lat: number; lng: number };
  phoneNumber: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ChainRowProps {
  chain: PharmacyChain;
  onEditChain: (chain: PharmacyChain) => void;
  onDeleteChain: (chain: PharmacyChain) => void;
  onAddBranch: (chainId: string) => void;
  onEditBranch: (branch: PharmacyBranch) => void;
  onDeleteBranch: (branch: PharmacyBranch) => void;
}

const ChainRow: React.FC<ChainRowProps> = ({
  chain,
  onEditChain,
  onDeleteChain,
  onAddBranch,
  onEditBranch,
  onDeleteBranch,
}) => {
  const [open, setOpen] = useState(false);
  const { data: branches, isLoading } = useQuery({
    queryKey: ['pharmacy-branches', chain.id],
    queryFn: async () => {
      const { data } = await apiClient.get<PharmacyBranch[]>(
        `/pharmacy-chains/${chain.id}/branches`
      );
      return data;
    },
    enabled: open,
  });

  return (
    <>
      <TableRow>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="body1" fontWeight={500}>
            {chain.name}
          </Typography>
        </TableCell>
        <TableCell>
          <Chip
            label={chain.integrationType}
            color={chain.integrationType === IntegrationType.CENTRAL ? 'primary' : 'secondary'}
            size="small"
          />
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {chain.baseURL || '—'}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Chip
            label={chain.branchCount || 0}
            color="default"
            size="small"
          />
        </TableCell>
        <TableCell>
          <Chip
            label={chain.isActive ? 'Active' : 'Inactive'}
            color={chain.isActive ? 'success' : 'default'}
            size="small"
          />
        </TableCell>
        <TableCell align="right">
          <IconButton size="small" color="primary" onClick={() => onEditChain(chain)}>
            <Edit />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => onDeleteChain(chain)}>
            <Delete />
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="h6" gutterBottom component="div">
                  Branches
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={() => onAddBranch(chain.id)}
                >
                  Add Branch
                </Button>
              </Box>
              {isLoading ? (
                <Box display="flex" justifyContent="center" p={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : branches && branches.length > 0 ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Address</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {branches.map((branch) => (
                      <TableRow key={branch.id}>
                        <TableCell>{branch.name}</TableCell>
                        <TableCell>{branch.address}</TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {branch.location.lat.toFixed(4)}, {branch.location.lng.toFixed(4)}
                          </Typography>
                        </TableCell>
                        <TableCell>{branch.phoneNumber || '—'}</TableCell>
                        <TableCell>
                          <Chip
                            label={branch.isActive ? 'Active' : 'Inactive'}
                            color={branch.isActive ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => onEditBranch(branch)}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => onDeleteBranch(branch)}
                          >
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography color="text.secondary" align="center" py={2}>
                  No branches found. Add one to get started.
                </Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export const PharmacyChainList: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Chain dialogs
  const [createChainDialogOpen, setCreateChainDialogOpen] = useState(false);
  const [editChainDialogOpen, setEditChainDialogOpen] = useState(false);
  const [deleteChainDialogOpen, setDeleteChainDialogOpen] = useState(false);
  const [selectedChain, setSelectedChain] = useState<PharmacyChain | null>(null);

  // Branch dialogs
  const [createBranchDialogOpen, setCreateBranchDialogOpen] = useState(false);
  const [editBranchDialogOpen, setEditBranchDialogOpen] = useState(false);
  const [deleteBranchDialogOpen, setDeleteBranchDialogOpen] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState<PharmacyBranch | null>(null);
  const [selectedChainIdForBranch, setSelectedChainIdForBranch] = useState<string>('');

  // Chain form fields
  const [chainName, setChainName] = useState('');
  const [integrationType, setIntegrationType] = useState<IntegrationType>(IntegrationType.CENTRAL);
  const [baseURL, setBaseURL] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [chainPhoneNumber, setChainPhoneNumber] = useState('');
  const [chainIsActive, setChainIsActive] = useState(true);

  // Branch form fields
  const [branchName, setBranchName] = useState('');
  const [remoteBranchCode, setRemoteBranchCode] = useState('');
  const [directBaseURL, setDirectBaseURL] = useState('');
  const [address, setAddress] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [branchIsActive, setBranchIsActive] = useState(true);

  const [error, setError] = useState<string | null>(null);

  // Fetch chains
  const { data: chains, isLoading, error: queryError } = useQuery({
    queryKey: ['pharmacy-chains'],
    queryFn: async () => {
      const { data } = await apiClient.get<PharmacyChain[]>('/pharmacy-chains');
      return data;
    },
  });

  // Chain mutations
  const createChainMutation = useMutation({
    mutationFn: async (chainData: any) => {
      await apiClient.post('/pharmacy-chains', chainData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-chains'] });
      setCreateChainDialogOpen(false);
      resetChainForm();
      setError(null);
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to create pharmacy chain');
    },
  });

  const updateChainMutation = useMutation({
    mutationFn: async ({ id, chainData }: { id: string; chainData: any }) => {
      await apiClient.put(`/pharmacy-chains/${id}`, chainData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-chains'] });
      setEditChainDialogOpen(false);
      setSelectedChain(null);
      resetChainForm();
      setError(null);
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to update pharmacy chain');
    },
  });

  const deleteChainMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/pharmacy-chains/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-chains'] });
      setDeleteChainDialogOpen(false);
      setSelectedChain(null);
      setError(null);
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to delete pharmacy chain');
    },
  });

  // Branch mutations
  const createBranchMutation = useMutation({
    mutationFn: async ({ chainId, branchData }: { chainId: string; branchData: any }) => {
      await apiClient.post(`/pharmacy-chains/${chainId}/branches`, branchData);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-branches', variables.chainId] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-chains'] });
      setCreateBranchDialogOpen(false);
      resetBranchForm();
      setError(null);
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to create pharmacy branch');
    },
  });

  const updateBranchMutation = useMutation({
    mutationFn: async ({ id, branchData }: { id: string; branchData: any }) => {
      await apiClient.put(`/pharmacy-chains/branches/${id}`, branchData);
    },
    onSuccess: () => {
      if (selectedBranch) {
        queryClient.invalidateQueries({ queryKey: ['pharmacy-branches', selectedBranch.chainId] });
      }
      setEditBranchDialogOpen(false);
      setSelectedBranch(null);
      resetBranchForm();
      setError(null);
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to update pharmacy branch');
    },
  });

  const deleteBranchMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/pharmacy-chains/branches/${id}`);
    },
    onSuccess: () => {
      if (selectedBranch) {
        queryClient.invalidateQueries({ queryKey: ['pharmacy-branches', selectedBranch.chainId] });
        queryClient.invalidateQueries({ queryKey: ['pharmacy-chains'] });
      }
      setDeleteBranchDialogOpen(false);
      setSelectedBranch(null);
      setError(null);
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to delete pharmacy branch');
    },
  });

  const resetChainForm = () => {
    setChainName('');
    setIntegrationType(IntegrationType.CENTRAL);
    setBaseURL('');
    setApiKey('');
    setLogoUrl('');
    setChainIsActive(true);
  };

  const resetBranchForm = () => {
    setBranchName('');
    setRemoteBranchCode('');
    setDirectBaseURL('');
    setAddress('');
    setLatitude('');
    setLongitude('');
    setPhoneNumber('');
    setBranchIsActive(true);
  };

  const handleCreateChainClick = () => {
    resetChainForm();
    setError(null);
    setCreateChainDialogOpen(true);
  };

  const handleEditChain = (chain: PharmacyChain) => {
    setSelectedChain(chain);
    setChainName(chain.name);
    setIntegrationType(chain.integrationType);
    setBaseURL(chain.baseURL || '');
    setApiKey('');
    setLogoUrl(chain.logoUrl || '');
    setChainPhoneNumber(chain.phoneNumber || '');
    setChainIsActive(chain.isActive);
    setError(null);
    setEditChainDialogOpen(true);
  };

  const handleDeleteChain = (chain: PharmacyChain) => {
    setSelectedChain(chain);
    setError(null);
    setDeleteChainDialogOpen(true);
  };

  const handleAddBranch = (chainId: string) => {
    setSelectedChainIdForBranch(chainId);
    resetBranchForm();
    setError(null);
    setCreateBranchDialogOpen(true);
  };

  const handleEditBranch = (branch: PharmacyBranch) => {
    setSelectedBranch(branch);
    setBranchName(branch.name);
    setRemoteBranchCode(branch.remoteBranchCode || '');
    setDirectBaseURL(branch.directBaseURL || '');
    setAddress(branch.address);
    setLatitude(branch.location.lat.toString());
    setLongitude(branch.location.lng.toString());
    setPhoneNumber(branch.phoneNumber || '');
    setBranchIsActive(branch.isActive);
    setError(null);
    setEditBranchDialogOpen(true);
  };

  const handleDeleteBranch = (branch: PharmacyBranch) => {
    setSelectedBranch(branch);
    setError(null);
    setDeleteBranchDialogOpen(true);
  };

  const handleCreateChainConfirm = () => {
    if (chainName.trim().length >= 2) {
      createChainMutation.mutate({
        name: chainName.trim(),
        integrationType,
        baseURL: baseURL.trim() || undefined,
        apiKey: apiKey.trim() || undefined,
        logoUrl: logoUrl.trim() || undefined,
        phoneNumber: chainPhoneNumber.trim() || undefined,
      });
    }
  };

  const handleEditChainConfirm = () => {
    if (selectedChain && chainName.trim().length >= 2) {
      updateChainMutation.mutate({
        id: selectedChain.id,
        chainData: {
          name: chainName.trim(),
          integrationType,
          baseURL: baseURL.trim() || undefined,
          apiKey: apiKey.trim() || undefined,
          logoUrl: logoUrl.trim() || undefined,
          phoneNumber: chainPhoneNumber.trim() || undefined,
          isActive: chainIsActive,
        },
      });
    }
  };

  const handleDeleteChainConfirm = () => {
    if (selectedChain) {
      deleteChainMutation.mutate(selectedChain.id);
    }
  };

  const handleCreateBranchConfirm = () => {
    if (
      branchName.trim().length >= 2 &&
      address.trim().length >= 5 &&
      latitude &&
      longitude
    ) {
      createBranchMutation.mutate({
        chainId: selectedChainIdForBranch,
        branchData: {
          name: branchName.trim(),
          remoteBranchCode: remoteBranchCode.trim() || undefined,
          directBaseURL: directBaseURL.trim() || undefined,
          address: address.trim(),
          location: {
            lat: parseFloat(latitude),
            lng: parseFloat(longitude),
          },
          phoneNumber: phoneNumber.trim() || undefined,
        },
      });
    }
  };

  const handleEditBranchConfirm = () => {
    if (
      selectedBranch &&
      branchName.trim().length >= 2 &&
      address.trim().length >= 5 &&
      latitude &&
      longitude
    ) {
      updateBranchMutation.mutate({
        id: selectedBranch.id,
        branchData: {
          name: branchName.trim(),
          remoteBranchCode: remoteBranchCode.trim() || undefined,
          directBaseURL: directBaseURL.trim() || undefined,
          address: address.trim(),
          location: {
            lat: parseFloat(latitude),
            lng: parseFloat(longitude),
          },
          phoneNumber: phoneNumber.trim() || undefined,
          isActive: branchIsActive,
        },
      });
    }
  };

  const handleDeleteBranchConfirm = () => {
    if (selectedBranch) {
      deleteBranchMutation.mutate(selectedBranch.id);
    }
  };

  const allChains = chains ?? [];

  return (
    <Box>
      {/* Hero */}
      <Box
        sx={{
          borderRadius: 4,
          bgcolor: NAVY,
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)',
          backgroundSize: '20px 20px',
          p: { xs: '28px 24px', sm: '40px 44px' },
          mb: 3,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ position: 'absolute', width: 260, height: 260, borderRadius: '50%', bgcolor: 'rgba(6,182,212,0.1)', top: -80, right: -60, pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', width: 140, height: 140, borderRadius: '50%', bgcolor: 'rgba(6,182,212,0.06)', bottom: -50, left: '35%', pointerEvents: 'none' }} />
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
              <IconButton size="small" onClick={() => navigate('/admin/dashboard')} sx={{ color: 'rgba(255,255,255,0.5)', p: 0.5 }}>
                <ArrowBack fontSize="small" />
              </IconButton>
              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase' }}>
                CADENAS
              </Typography>
            </Box>
            <Typography variant="h2" sx={{ color: '#fff', mb: 0.5 }}>
              Cadenas de Farmacias
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: { xs: 13, sm: 14 } }}>
              {allChains.length} cadenas registradas
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateChainClick}
            sx={{ bgcolor: CYAN, flexShrink: 0, mt: { xs: 0, sm: 1 }, color: '#fff', '&:hover': { bgcolor: '#0891b2' } }}
          >
            {isMobile ? 'Agregar' : 'Agregar Cadena'}
          </Button>
        </Box>
      </Box>

      {queryError && <Alert severity="error" sx={{ mb: 2 }}>Failed to load pharmacy chains. Please try again.</Alert>}

      {isLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {allChains.length > 0 ? (
            allChains.map((chain) => (
              <Card key={chain.id} elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: `${CYAN}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: CYAN, flexShrink: 0 }}>
                      <Store sx={{ fontSize: 18 }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 600, color: NAVY, fontSize: 15 }} noWrap>{chain.name}</Typography>
                      <Box sx={{ display: 'flex', gap: 0.75, mt: 0.5, flexWrap: 'wrap' }}>
                        <Chip label={chain.integrationType} color={chain.integrationType === IntegrationType.CENTRAL ? 'primary' : 'secondary'} size="small" />
                        <Chip label={chain.isActive ? 'Activa' : 'Inactiva'} color={chain.isActive ? 'success' : 'default'} size="small" />
                        <Chip label={`${chain.branchCount || 0} sucursales`} size="small" />
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                    <IconButton size="small" color="primary" onClick={() => handleEditChain(chain)}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDeleteChain(chain)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Card>
            ))
          ) : (
            <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
              <Store sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
              <Typography>No pharmacy chains found. Create one to get started.</Typography>
            </Box>
          )}
        </Box>
      ) : (
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Name</TableCell>
                <TableCell>Integration Type</TableCell>
                <TableCell>Base URL</TableCell>
                <TableCell align="center">Branches</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allChains.length > 0 ? (
                allChains.map((chain) => (
                  <ChainRow
                    key={chain.id}
                    chain={chain}
                    onEditChain={handleEditChain}
                    onDeleteChain={handleDeleteChain}
                    onAddBranch={handleAddBranch}
                    onEditBranch={handleEditBranch}
                    onDeleteBranch={handleDeleteBranch}
                  />
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary" py={4}>
                      No pharmacy chains found. Create one to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      )}

      {/* Create Chain Dialog */}
      <Dialog open={createChainDialogOpen} onClose={() => setCreateChainDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create Pharmacy Chain</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter the pharmacy chain details.
          </DialogContentText>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                label="Chain Name"
                fullWidth
                value={chainName}
                onChange={(e) => setChainName(e.target.value)}
                error={chainName.length > 0 && chainName.length < 2}
                helperText={
                  chainName.length > 0 && chainName.length < 2
                    ? 'Name must be at least 2 characters'
                    : ''
                }
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Integration Type</InputLabel>
                <Select
                  value={integrationType}
                  label="Integration Type"
                  onChange={(e) => setIntegrationType(e.target.value as IntegrationType)}
                >
                  <MenuItem value={IntegrationType.CENTRAL}>Central</MenuItem>
                  <MenuItem value={IntegrationType.DISTRIBUTED}>Distributed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Base URL (Optional)"
                fullWidth
                value={baseURL}
                onChange={(e) => setBaseURL(e.target.value)}
                placeholder="https://api.example.com"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="API Key (Optional)"
                fullWidth
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Enter API key"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Logo URL (Optional)"
                fullWidth
                value={logoUrl}
                onChange={(e) => setLogoUrl(e.target.value)}
                placeholder="https://example.com/logo.png"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="WhatsApp / Phone Number (Optional)"
                fullWidth
                value={chainPhoneNumber}
                onChange={(e) => setChainPhoneNumber(e.target.value)}
                placeholder="+1 809 555 1234"
                helperText="Used as the WhatsApp link customers see for delivery orders"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateChainDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateChainConfirm}
            variant="contained"
            disabled={chainName.trim().length < 2 || createChainMutation.isPending}
          >
            {createChainMutation.isPending ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Chain Dialog */}
      <Dialog open={editChainDialogOpen} onClose={() => setEditChainDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Pharmacy Chain</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Update the pharmacy chain details.
          </DialogContentText>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                label="Chain Name"
                fullWidth
                value={chainName}
                onChange={(e) => setChainName(e.target.value)}
                error={chainName.length > 0 && chainName.length < 2}
                helperText={
                  chainName.length > 0 && chainName.length < 2
                    ? 'Name must be at least 2 characters'
                    : ''
                }
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Integration Type</InputLabel>
                <Select
                  value={integrationType}
                  label="Integration Type"
                  onChange={(e) => setIntegrationType(e.target.value as IntegrationType)}
                >
                  <MenuItem value={IntegrationType.CENTRAL}>Central</MenuItem>
                  <MenuItem value={IntegrationType.DISTRIBUTED}>Distributed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Base URL (Optional)"
                fullWidth
                value={baseURL}
                onChange={(e) => setBaseURL(e.target.value)}
                placeholder="https://api.example.com"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="API Key (Optional - leave blank to keep existing)"
                fullWidth
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Enter new API key"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Logo URL (Optional)"
                fullWidth
                value={logoUrl}
                onChange={(e) => setLogoUrl(e.target.value)}
                placeholder="https://example.com/logo.png"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="WhatsApp / Phone Number (Optional)"
                fullWidth
                value={chainPhoneNumber}
                onChange={(e) => setChainPhoneNumber(e.target.value)}
                placeholder="+1 809 555 1234"
                helperText="Used as the WhatsApp link customers see for delivery orders"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={chainIsActive ? 'active' : 'inactive'}
                  label="Status"
                  onChange={(e) => setChainIsActive(e.target.value === 'active')}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditChainDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleEditChainConfirm}
            variant="contained"
            disabled={chainName.trim().length < 2 || updateChainMutation.isPending}
          >
            {updateChainMutation.isPending ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Chain Dialog */}
      <Dialog open={deleteChainDialogOpen} onClose={() => setDeleteChainDialogOpen(false)}>
        <DialogTitle>Delete Pharmacy Chain</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <DialogContentText>
            Are you sure you want to delete <strong>{selectedChain?.name}</strong>?
            {selectedChain && selectedChain.branchCount && selectedChain.branchCount > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                This chain has {selectedChain.branchCount} branch(es). You must delete all branches first.
              </Alert>
            )}
            {!error && (!selectedChain?.branchCount || selectedChain.branchCount === 0) && ' This action cannot be undone.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteChainDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteChainConfirm}
            color="error"
            variant="contained"
            disabled={deleteChainMutation.isPending}
          >
            {deleteChainMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Branch Dialog */}
      <Dialog open={createBranchDialogOpen} onClose={() => setCreateBranchDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create Pharmacy Branch</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter the pharmacy branch details.
          </DialogContentText>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                label="Branch Name"
                fullWidth
                value={branchName}
                onChange={(e) => setBranchName(e.target.value)}
                error={branchName.length > 0 && branchName.length < 2}
                helperText={
                  branchName.length > 0 && branchName.length < 2
                    ? 'Name must be at least 2 characters'
                    : ''
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Address"
                fullWidth
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                error={address.length > 0 && address.length < 5}
                helperText={
                  address.length > 0 && address.length < 5
                    ? 'Address must be at least 5 characters'
                    : ''
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Latitude"
                fullWidth
                type="number"
                value={latitude}
                onChange={(e) => setLatitude(e.target.value)}
                placeholder="18.4861"
                inputProps={{ step: 'any' }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Longitude"
                fullWidth
                type="number"
                value={longitude}
                onChange={(e) => setLongitude(e.target.value)}
                placeholder="-69.9312"
                inputProps={{ step: 'any' }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Remote Branch Code (Optional)"
                fullWidth
                value={remoteBranchCode}
                onChange={(e) => setRemoteBranchCode(e.target.value)}
                placeholder="BRANCH-001"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Direct Base URL (Optional)"
                fullWidth
                value={directBaseURL}
                onChange={(e) => setDirectBaseURL(e.target.value)}
                placeholder="https://branch.example.com"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Phone Number (Optional)"
                fullWidth
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="809-555-1234"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateBranchDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateBranchConfirm}
            variant="contained"
            disabled={
              branchName.trim().length < 2 ||
              address.trim().length < 5 ||
              !latitude ||
              !longitude ||
              createBranchMutation.isPending
            }
          >
            {createBranchMutation.isPending ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Branch Dialog */}
      <Dialog open={editBranchDialogOpen} onClose={() => setEditBranchDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Pharmacy Branch</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Update the pharmacy branch details.
          </DialogContentText>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                label="Branch Name"
                fullWidth
                value={branchName}
                onChange={(e) => setBranchName(e.target.value)}
                error={branchName.length > 0 && branchName.length < 2}
                helperText={
                  branchName.length > 0 && branchName.length < 2
                    ? 'Name must be at least 2 characters'
                    : ''
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Address"
                fullWidth
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                error={address.length > 0 && address.length < 5}
                helperText={
                  address.length > 0 && address.length < 5
                    ? 'Address must be at least 5 characters'
                    : ''
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Latitude"
                fullWidth
                type="number"
                value={latitude}
                onChange={(e) => setLatitude(e.target.value)}
                placeholder="18.4861"
                inputProps={{ step: 'any' }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Longitude"
                fullWidth
                type="number"
                value={longitude}
                onChange={(e) => setLongitude(e.target.value)}
                placeholder="-69.9312"
                inputProps={{ step: 'any' }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Remote Branch Code (Optional)"
                fullWidth
                value={remoteBranchCode}
                onChange={(e) => setRemoteBranchCode(e.target.value)}
                placeholder="BRANCH-001"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Direct Base URL (Optional)"
                fullWidth
                value={directBaseURL}
                onChange={(e) => setDirectBaseURL(e.target.value)}
                placeholder="https://branch.example.com"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Phone Number (Optional)"
                fullWidth
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="809-555-1234"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={branchIsActive ? 'active' : 'inactive'}
                  label="Status"
                  onChange={(e) => setBranchIsActive(e.target.value === 'active')}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditBranchDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleEditBranchConfirm}
            variant="contained"
            disabled={
              branchName.trim().length < 2 ||
              address.trim().length < 5 ||
              !latitude ||
              !longitude ||
              updateBranchMutation.isPending
            }
          >
            {updateBranchMutation.isPending ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Branch Dialog */}
      <Dialog open={deleteBranchDialogOpen} onClose={() => setDeleteBranchDialogOpen(false)}>
        <DialogTitle>Delete Pharmacy Branch</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <DialogContentText>
            Are you sure you want to delete <strong>{selectedBranch?.name}</strong>?
            {!error && ' This action cannot be undone.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteBranchDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteBranchConfirm}
            color="error"
            variant="contained"
            disabled={deleteBranchMutation.isPending}
          >
            {deleteBranchMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
